import { LeagueTableRecord } from '@repo/types';
import { ClubNameType } from '../../../fixtures/contracts';

export enum ColumnType {
  Number = 'number',
  String = 'string',
  TeamName = 'teamName',
  NotAvailable = 'notAvailable',
}

export enum LeagueTableColumn {
  Rank = 'Rank',
  Team = 'Team',
  GP = 'GP',
  P = 'P',
  HW = 'HW',
  HD = 'HD',
  HL = 'HL',
  AW = 'AW',
  AD = 'AD',
  AL = 'AL',
  W = 'W',
  D = 'D',
  L = 'L',
  GF = 'GF',
  GA = 'GA',
  GD = 'GD',
}

export const defaultLeagueTableColumns = [
  LeagueTableColumn.Rank,
  LeagueTableColumn.Team,
  LeagueTableColumn.GP,
  LeagueTableColumn.P,
  LeagueTableColumn.HW,
  LeagueTableColumn.HD,
  LeagueTableColumn.HL,
  LeagueTableColumn.AW,
  LeagueTableColumn.AD,
  LeagueTableColumn.AL,
  LeagueTableColumn.W,
  LeagueTableColumn.D,
  LeagueTableColumn.L,
  LeagueTableColumn.GF,
  LeagueTableColumn.GA,
  LeagueTableColumn.GD,
];

export const leagueTableColumns: { label: LeagueTableColumn; type: ColumnType }[] = [
  {
    label: LeagueTableColumn.Rank,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.Team,
    type: ColumnType.TeamName,
  },
  {
    label: LeagueTableColumn.GP,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.P,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.HW,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.HD,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.HL,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.AW,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.AD,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.AL,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.W,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.D,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.L,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.GF,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.GA,
    type: ColumnType.Number,
  },
  {
    label: LeagueTableColumn.GD,
    type: ColumnType.Number,
  },
];

const getTeamName = ({ team, nameType }: { team: LeagueTableRecord['team']; nameType: ClubNameType }) => {
  if (!team) return 'N/A';
  switch (nameType) {
    case ClubNameType.ThreeLetterName:
      return team.threeLetterName ?? team.shortName ?? team.fullName ?? 'Unknown';
    case ClubNameType.ShortName:
      return team.shortName ?? team.fullName ?? team.threeLetterName ?? 'Unknown';
    case ClubNameType.FullName:
      return team.fullName ?? team.shortName ?? team.threeLetterName ?? 'Unknown';
    case ClubNameType.NickName:
      return team.nickName ?? team.shortName ?? team.fullName ?? 'Unknown';
    default:
      return team.shortName ?? team.fullName ?? team.threeLetterName ?? 'Unknown';
  }
};

export const mapLeagueTableColumnData = (
  column: LeagueTableColumn,
  leagueTableRecord: LeagueTableRecord,
  nameType: ClubNameType
) => {
  switch (column) {
    case LeagueTableColumn.Rank:
      return {
        label: LeagueTableColumn.Rank,
        type: ColumnType.Number,
        data: leagueTableRecord.position,
      };
    case LeagueTableColumn.Team:
      return {
        label: LeagueTableColumn.Team,
        type: ColumnType.TeamName,
        data: getTeamName({ team: leagueTableRecord.team, nameType }),
        deductedPoints: leagueTableRecord.deductedPoints,
      };
    case LeagueTableColumn.GP:
      return {
        label: LeagueTableColumn.GP,
        type: ColumnType.Number,
        data: leagueTableRecord.total.played,
      };
    case LeagueTableColumn.P:
      return {
        label: LeagueTableColumn.P,
        type: ColumnType.Number,
        data: leagueTableRecord.points,
      };
    case LeagueTableColumn.HW:
      return {
        label: LeagueTableColumn.HW,
        type: ColumnType.Number,
        data: leagueTableRecord.home.wins,
      };
    case LeagueTableColumn.HD:
      return {
        label: LeagueTableColumn.HD,
        type: ColumnType.Number,
        data: leagueTableRecord.home.draws,
      };
    case LeagueTableColumn.HL:
      return {
        label: LeagueTableColumn.HL,
        type: ColumnType.Number,
        data: leagueTableRecord.home.losses,
      };
    case LeagueTableColumn.AW:
      return {
        label: LeagueTableColumn.AW,
        type: ColumnType.Number,
        data: leagueTableRecord.away.wins,
      };
    case LeagueTableColumn.AD:
      return {
        label: LeagueTableColumn.AD,
        type: ColumnType.Number,
        data: leagueTableRecord.away.draws,
      };
    case LeagueTableColumn.AL:
      return {
        label: LeagueTableColumn.AL,
        type: ColumnType.Number,
        data: leagueTableRecord.away.losses,
      };
    case LeagueTableColumn.W:
      return {
        label: LeagueTableColumn.W,
        type: ColumnType.Number,
        data: leagueTableRecord.total.wins,
      };
    case LeagueTableColumn.D:
      return {
        label: LeagueTableColumn.D,
        type: ColumnType.Number,
        data: leagueTableRecord.total.draws,
      };
    case LeagueTableColumn.L:
      return {
        label: LeagueTableColumn.L,
        type: ColumnType.Number,
        data: leagueTableRecord.total.losses,
      };
    case LeagueTableColumn.GF:
      return {
        label: LeagueTableColumn.GF,
        type: ColumnType.Number,
        data: leagueTableRecord.total.scored,
      };
    case LeagueTableColumn.GA:
      return {
        label: LeagueTableColumn.GA,
        type: ColumnType.Number,
        data: leagueTableRecord.total.conceded,
      };
    case LeagueTableColumn.GD:
      return {
        label: LeagueTableColumn.GD,
        type: ColumnType.Number,
        data: leagueTableRecord.total.goalDifference,
      };
    default:
      return {
        label: column,
        type: ColumnType.NotAvailable,
        data: 'N/A',
      };
  }
};

export const mapLeagueTableColumnsData = (
  columns: LeagueTableColumn[],
  leagueTableRecord: LeagueTableRecord,
  nameType: ClubNameType
) => {
  return columns.map((column) => mapLeagueTableColumnData(column, leagueTableRecord, nameType));
};
